/**
 * Print service: initiatePrint, printImmediately, processPrintingNonBlocking.
 * Orthogonal to upload pipeline; pages call directly.
 */

import { PrintUtils } from '@renderer/utils/printUtils'
import type { PrintSettings } from '@renderer/stores/printStore'
import { logPrintEvent } from '@renderer/utils/printEventLogger'

export type PrintEventType = 'session' | 'user_reprint' | 'operator_reprint' | 'test_print'

export async function initiatePrint(
  stripBlob: Blob,
  printerName: string,
  copies: number,
  settings: PrintSettings,
  isOnline: boolean = true,
  duplicateCheckId?: string,
  eventType: PrintEventType = 'session'
): Promise<void> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = async (): Promise<void> => {
      try {
        const base64data = reader.result as string
        const printResult = await PrintUtils.sendToPrinter(base64data, {
          printerName,
          copies,
          pageSize: settings.pageSize,
          orientation: settings.orientation,
          margins: settings.margins,
          dpi: settings.dpi,
          scaleFactor: settings.scaleFactor,
          printBackground: settings.printBackground,
          printResolutionScale: settings.printResolutionScale,
          printPositionShift: settings.printPositionShift,
          duplicateCheckId
        })

        if (printResult.success) {
          void logPrintEvent({
            eventType,
            copies,
            printerName,
            success: true
          })
          resolve()
        } else {
          const errorMsg =
            printResult.failureReason || printResult.error || 'Unknown error'
          void logPrintEvent({ eventType, copies, printerName, success: false, error: errorMsg })
          const isNetworkError =
            !isOnline &&
            (printResult.error?.includes('network') ||
              printResult.error?.includes('fetch') ||
              printResult.failureReason?.includes('network'))
          if (!isNetworkError || isOnline) {
            reject(new Error(errorMsg))
          } else {
            resolve()
          }
        }
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : 'Unknown error'
        void logPrintEvent({ eventType, copies, printerName, success: false, error: errMsg })
        reject(error instanceof Error ? error : new Error('Unknown error'))
      }
    }
    reader.onerror = (): void => {
      const errorMsg = 'Failed to read file for printing'
      void logPrintEvent({ eventType, copies, printerName, success: false, error: errorMsg })
      reject(new Error(errorMsg))
    }
    reader.readAsDataURL(stripBlob)
  })
}

export function printImmediately(
  stripBlob: Blob,
  printerName: string,
  copies: number,
  settings: PrintSettings,
  onSuccess?: () => void,
  onError?: (error: string) => void,
  isOnline: boolean = true,
  eventType: PrintEventType = 'session'
): void {
  const reader = new FileReader()
  reader.onloadend = async (): Promise<void> => {
    try {
      const base64data = reader.result as string
      const printResult = await PrintUtils.sendToPrinter(base64data, {
        printerName,
        copies,
        pageSize: settings.pageSize,
        orientation: settings.orientation,
        margins: settings.margins,
        dpi: settings.dpi,
        scaleFactor: settings.scaleFactor,
        printBackground: settings.printBackground,
        printResolutionScale: settings.printResolutionScale,
        printPositionShift: settings.printPositionShift
      })
      if (printResult.success) {
        void logPrintEvent({ eventType, copies, printerName, success: true })
        onSuccess?.()
      } else {
        const errorMsg = printResult.failureReason || printResult.error || 'Unknown error'
        void logPrintEvent({ eventType, copies, printerName, success: false, error: errorMsg })
        const isNetworkError =
          !isOnline &&
          (printResult.error?.includes('network') ||
            printResult.error?.includes('fetch') ||
            printResult.failureReason?.includes('network'))
        if (!isNetworkError || isOnline) onError?.(errorMsg)
      }
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : 'Unknown error'
      void logPrintEvent({ eventType, copies, printerName, success: false, error: errMsg })
      onError?.(errMsg)
    }
  }
  reader.onerror = (): void => {
    const errorMsg = 'Failed to read file for printing'
    void logPrintEvent({ eventType, copies, printerName, success: false, error: errorMsg })
    onError?.(errorMsg)
  }
  reader.readAsDataURL(stripBlob)
}

export async function processPrintingNonBlocking(
  stripBlob: Blob,
  printerName: string,
  copies: number,
  settings: PrintSettings,
  onSuccess?: () => void,
  onError?: (error: string) => void,
  isOnline: boolean = true
): Promise<void> {
  printImmediately(stripBlob, printerName, copies, settings, onSuccess, onError, isOnline)
}
